// ===========================================
// API error messages
// ===========================================

/**
 * The message the API actually sent, not axios's status line.
 *
 * ⚠️ `error.message` on a rejected axios call is "Request failed with status
 * code 422" — the useful text is at `response.data.message`. That matters
 * wherever the server's refusal is the whole point of the response: a
 * merge-field 422 NAMES the offending token ("Unknown merge field:
 * {{frstName}}. Available: …"), and showing a bare status code throws away the
 * only actionable part.
 *
 * This is the shared version. `utils/timeOff.ts` has an older hand-rolled copy
 * (`timeOffErrorMessage`); prefer this one for new call sites rather than
 * writing a third.
 */
export function apiErrorMessage(error: unknown, fallback = 'Unknown error'): string {
  const maybe = error as { response?: { data?: { message?: string } }; message?: string };
  return maybe?.response?.data?.message || maybe?.message || fallback;
}

/**
 * The machine-readable reason the API attached to a refusal, e.g.
 * `'PAYMENT_RECORDED'` — carried in `response.data.details.code` by
 * `AppError(message, status, { code, ... })`.
 */
export function apiErrorCode(error: unknown): string | undefined {
  const code = apiErrorDetails<{ code?: unknown }>(error)?.code;
  return typeof code === 'string' ? code : undefined;
}

/** The `details` object an `AppError` carried, if any. */
export function apiErrorDetails<T = Record<string, unknown>>(error: unknown): T | undefined {
  const maybe = error as { response?: { data?: { details?: unknown } } };
  const details = maybe?.response?.data?.details;
  return details && typeof details === 'object' ? (details as T) : undefined;
}
